import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Car, CalendarDays, Coins, MapPin, ShieldCheck, CheckCircle2, PlayCircle, Undo2, Briefcase, AlertTriangle, Star } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatZAR, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { vehiclePhoto } from "../lib/photos";
import DriverCalendar from "../components/DriverCalendar";
import DriverStatements from "../components/DriverStatements";

export default function DriverDashboard() {
  const { user, ready, setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (ready && (!user || user === false)) navigate("/login?next=/driver");
    if (ready && user?.id && user.role !== "driver" && user.role !== "admin") navigate("/dashboard");
  }, [user, ready, navigate]);

  const refresh = () => {
    api.get("/driver/jobs").then((r) => setJobs(r.data || [])).catch(() => {});
    api.get("/driver/trips").then((r) => setTrips(r.data || [])).catch(() => {});
    api.get("/driver/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.id) refresh(); }, [user?.id]);

  if (!user?.id) return null;
  const profile = user.driver_profile || { available: false };

  const toggleAvailable = async () => {
    try {
      const { data } = await api.patch("/driver/profile", { available: !profile.available });
      setUser(data);
      toast.success(data.driver_profile.available ? "You're available for trips" : "You're now offline");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };

  const accept = async (id) => {
    setBusy(id);
    try {
      await api.post(`/driver/jobs/${id}/accept`);
      toast.success("Trip accepted — it's in your schedule");
      refresh();
      setTab("trips");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not accept trip");
    } finally {
      setBusy(null);
    }
  };

  const setStatus = async (id, driver_status) => {
    setBusy(id);
    try {
      await api.post(`/driver/trips/${id}/status`, { driver_status });
      toast.success(driver_status === "released" ? "Trip released back to the board" : `Trip ${driver_status.replace("_", " ")}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update trip");
    } finally {
      setBusy(null);
    }
  };

  const canAccept = user.licence_verified && user.police_clearance_verified && profile.available;

  return (
    <div className="relative min-h-[calc(100vh-4rem)]" data-testid="driver-dashboard">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Chauffeur dashboard</p>
            <h1 className="font-brand text-3xl sm:text-4xl text-[#0a1f4d] mt-2">Welcome, {(user.name || "driver").split(" ")[0]}</h1>
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0a1f4d]" data-testid="driver-rating-summary">
              <Star size={14} className="fill-[#0a1f4d]" />
              {stats?.rating_count ? `${stats.rating_avg} · ${stats.rating_count} rating${stats.rating_count === 1 ? "" : "s"}` : "No ratings yet"}
            </p>
          </div>
          <button
            onClick={toggleAvailable}
            className={`inline-flex items-center gap-3 rounded-full border px-4 py-2.5 text-sm font-bold transition ${profile.available ? "border-[#10B981] bg-[#10B981]/10 text-[#065F46]" : "border-[#E2E8F0] bg-white text-[#0a1f4d]"}`}
            data-testid="driver-availability-toggle"
          >
            <span className={`w-2.5 h-2.5 rounded-full ${profile.available ? "bg-[#10B981]" : "bg-[#94A3B8]"}`} />
            {profile.available ? "Available for trips" : "Offline"}
          </button>
        </div>

        {(!user.licence_verified || !user.police_clearance_verified) && (
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-[#F59E0B]/40 bg-[#FFFBEB] p-4" data-testid="driver-verify-banner">
            <AlertTriangle size={18} className="text-[#B45309] mt-0.5" />
            <div className="text-sm text-[#78350F]">
              <p className="font-bold">
                {!user.licence_verified && !user.police_clearance_verified
                  ? "Verify your driver licence and police clearance certificate to accept trips."
                  : !user.licence_verified
                    ? "Verify your driver licence to accept trips."
                    : user.police_clearance_expires_on && user.police_clearance_expires_on < new Date().toISOString().slice(0, 10)
                      ? "Your police clearance certificate has expired — upload a new one to keep accepting trips."
                      : "Upload your SAPS police clearance certificate (issued within 6 months) to accept trips."}
              </p>
              <p className="mt-0.5">Our team approves documents within a day. <Link to="/dashboard/profile" className="font-semibold underline" data-testid="driver-verify-link">Go to verification</Link></p>
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Open jobs" value={jobs.length} icon={<Briefcase size={18} />} />
          <Stat label="Upcoming trips" value={stats?.trips_upcoming ?? 0} icon={<CalendarDays size={18} />} />
          <Stat label="Completed" value={stats?.trips_completed ?? 0} icon={<CheckCircle2 size={18} />} />
          <Stat label="Earnings" value={formatZAR(stats?.earnings_zar || 0)} icon={<Coins size={18} />} />
        </div>

        <div className="mt-8 border-b border-[#E2E8F0] flex gap-6">
          {[{ key: "jobs", label: `Job board (${jobs.length})` }, { key: "trips", label: `My trips (${trips.length})` }, { key: "calendar", label: "Calendar" }, { key: "statements", label: "Statements" }, { key: "profile", label: "Profile" }].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`pb-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#1d7ffd] text-[#0a1f4d]" : "border-transparent text-[#64748B] hover:text-[#0a1f4d]"}`} data-testid={`driver-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "jobs" && (
          <div className="mt-6 space-y-3">
            {jobs.length === 0 && <Empty title="No open chauffeur jobs right now" body="When a customer books a car with a chauffeur, the trip appears here first-come, first-served. Jobs overlapping your blocked dates or accepted trips are hidden." />}
            {jobs.map((j) => (
              <JobRow key={j.id} j={j}>
                <button onClick={() => accept(j.id)} disabled={!canAccept || busy === j.id} className="btn-primary text-sm !py-2.5 disabled:opacity-50" data-testid={`accept-job-${j.id}`} title={!canAccept ? "Verify your licence + police clearance and set yourself available" : ""}>
                  {busy === j.id ? "Accepting…" : "Accept trip"}
                </button>
              </JobRow>
            ))}
          </div>
        )}

        {tab === "trips" && (
          <div className="mt-6 space-y-3">
            {trips.length === 0 && <Empty title="No trips yet" body="Accept a job from the board and it will show up in your schedule." />}
            {trips.map((t) => (
              <JobRow key={t.id} j={t} showStatus>
                {t.driver_status === "assigned" && (
                  <>
                    <button onClick={() => setStatus(t.id, "released")} disabled={busy === t.id} className="btn-outline text-xs !py-2 !px-3" data-testid={`release-trip-${t.id}`}><Undo2 size={12} /> Release</button>
                    <button onClick={() => setStatus(t.id, "in_progress")} disabled={busy === t.id} className="btn-primary text-sm !py-2.5" data-testid={`start-trip-${t.id}`}><PlayCircle size={14} /> Start trip</button>
                  </>
                )}
                {t.driver_status === "in_progress" && (
                  <button onClick={() => setStatus(t.id, "completed")} disabled={busy === t.id} className="btn-primary text-sm !py-2.5" data-testid={`complete-trip-${t.id}`}><CheckCircle2 size={14} /> Complete</button>
                )}
                {t.driver_status === "completed" && t.driver_rating && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-[#0a1f4d] bg-[#F7F8FA] rounded-full px-2.5 py-1" data-testid={`trip-rating-${t.id}`}><Star size={11} className="fill-[#0a1f4d]" /> {t.driver_rating}/5</span>
                )}
              </JobRow>
            ))}
          </div>
        )}

        {tab === "calendar" && <DriverCalendar onChanged={refresh} />}
        {tab === "statements" && <div className="mt-6"><DriverStatements /></div>}

        {tab === "profile" && (
          <>
            <ProfileForm profile={profile} onSaved={setUser} user={user} />
            <div className="mt-5 rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="driver-reviews">
              <p className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Customer reviews</p>
              {!stats?.reviews?.length && <p className="text-sm text-[#64748B] mt-2">Complete trips to start collecting ratings.</p>}
              <div className="mt-3 space-y-3">
                {(stats?.reviews || []).map((r) => (
                  <div key={r.id} className="border-t border-[#EEF2F7] pt-3 first:border-0 first:pt-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="inline-flex items-center gap-0.5 font-bold text-[#0a1f4d]"><Star size={12} className="fill-[#0a1f4d]" /> {r.rating}</span>
                      <span className="font-semibold text-[#0a1f4d]">{r.customer_name}</span>
                      <span className="text-xs text-[#64748B]">· {r.vehicle_title}</span>
                    </div>
                    {r.comment && <p className="text-sm text-[#334155] mt-1">{r.comment}</p>}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon }) => (
  <div className="rounded-2xl border border-[#E2E8F0] p-5 bg-white">
    <div className="text-[#1d7ffd]">{icon}</div>
    <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B] mt-3">{label}</p>
    <p className="font-display font-black mt-1 text-2xl text-[#0a1f4d]">{value}</p>
  </div>
);

const Empty = ({ title, body }) => (
  <div className="border border-[#E2E8F0] rounded-2xl p-10 text-center bg-white">
    <p className="font-display font-bold text-[#0a1f4d]">{title}</p>
    <p className="text-sm text-[#64748B] mt-1">{body}</p>
  </div>
);

const STATUS = {
  assigned: { cls: "bg-[#1d7ffd]/10 text-[#1d7ffd]", label: "Upcoming" },
  in_progress: { cls: "bg-[#F59E0B]/15 text-[#B45309]", label: "In progress" },
  completed: { cls: "bg-[#10B981]/15 text-[#065F46]", label: "Completed" },
};

function JobRow({ j, children, showStatus }) {
  const s = STATUS[j.driver_status];
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-2xl border border-[#E2E8F0] bg-white" data-testid={`job-row-${j.id}`}>
      <div className="w-full sm:w-28 aspect-[4/3] rounded-xl overflow-hidden bg-[#F0F2F5] flex-shrink-0">
        <img src={j.vehicle_photo || vehiclePhoto({})} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-display font-bold text-[#0a1f4d] truncate">{j.vehicle_title}</p>
          {showStatus && s && <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${s.cls}`}>{s.label}</span>}
        </div>
        <p className="text-xs text-[#64748B] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {format(new Date(j.start_date), "EEE d MMM")} – {format(new Date(j.end_date), "EEE d MMM")} · {j.days} day{j.days === 1 ? "" : "s"}</span>
          {j.city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {j.city}</span>}
          {j.customer_name && <span className="inline-flex items-center gap-1"><ShieldCheck size={12} /> {j.customer_name}</span>}
        </p>
      </div>
      <div className="flex items-center gap-3 sm:text-right">
        <div className="mr-auto sm:mr-0">
          <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B]">Your fee</p>
          <p className="font-display font-black text-[#0a1f4d]">{formatZAR(j.driver_fee)}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

function ProfileForm({ profile, user, onSaved }) {
  const [form, setForm] = useState({ experience_years: profile.experience_years || 0, cities: (profile.cities || []).join(", "), bio: profile.bio || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/driver/profile", { experience_years: Number(form.experience_years) || 0, cities: form.cities.split(",").map((s) => s.trim()).filter(Boolean), bio: form.bio });
      onSaved(data);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 rounded-2xl border border-[#E2E8F0] bg-white p-6 space-y-4">
        <label className="block">
          <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Years of experience</span>
          <input type="number" min="0" className="ip mt-1.5" value={form.experience_years} onChange={(e) => setForm((f) => ({ ...f, experience_years: e.target.value }))} data-testid="driver-exp-input" />
        </label>
        <label className="block">
          <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Cities you serve (comma separated)</span>
          <input className="ip mt-1.5" value={form.cities} onChange={(e) => setForm((f) => ({ ...f, cities: e.target.value }))} placeholder="Johannesburg, Pretoria" data-testid="driver-cities-input" />
        </label>
        <label className="block">
          <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Short bio</span>
          <textarea className="ip mt-1.5 min-h-[100px]" value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} placeholder="PrDP licensed, executive and airport transfers…" data-testid="driver-bio-input" />
        </label>
        <button onClick={save} disabled={saving} className="btn-primary text-sm disabled:opacity-60" data-testid="driver-profile-save">{saving ? "Saving…" : "Save profile"}</button>
        <style>{`.ip{width:100%;background:#F7F8FA;border:1px solid #E2E8F0;border-radius:0.75rem;padding:10px 14px;font-size:14px;color:#0a1f4d;outline:none}.ip:focus{border-color:#1d7ffd}`}</style>
      </div>
      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
        <p className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Verification</p>
        <div className="mt-3 space-y-2 text-sm">
          <Row ok={user.id_verified} label="ID document" />
          <Row ok={user.licence_verified} label="Driver licence" />
          <Row ok={user.police_clearance_verified} label="Police clearance certificate" />
        </div>
        <Link to="/dashboard/profile" className="btn-outline text-sm mt-5 inline-flex !py-2.5" data-testid="driver-verify-link"><Car size={14} /> Manage documents</Link>
      </div>
    </div>
  );
}

const Row = ({ ok, label }) => (
  <div className="flex items-center justify-between">
    <span className="text-[#0a1f4d]">{label}</span>
    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${ok ? "bg-[#10B981]/15 text-[#065F46]" : "bg-[#F59E0B]/15 text-[#B45309]"}`}>{ok ? "Verified" : "Pending"}</span>
  </div>
);
